import { useState } from "react";
import {
  doc,
  collection,
  addDoc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

export interface DuplicateResult {
  newProjectID: string;
  stepsCopied: number;
}

/**
 * Hook to duplicate an existing project — copies metadata and all 24 step forms.
 */
export function useProjectDuplicate() {
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const duplicateProject = async (
    userID: string,
    sourceProjectID: string,
    sourceTitle: string
  ): Promise<DuplicateResult | null> => {
    setIsDuplicating(true);
    setError(null);

    try {
      const userRef = doc(db, "users", userID);

      // 1. Read all step data from source project
      const sourceProgressRef = collection(userRef, "projects", sourceProjectID, "progress");
      const progressSnap = await getDocs(sourceProgressRef);

      // 2. Create new project doc
      const projectsRef = collection(userRef, "projects");
      const newProjectDoc = await addDoc(projectsRef, {
        projectTitle: `${sourceTitle} (Copy)`,
        projectProgress: 0,
        projectDescription: "",
        projectMotivation: "",
        aiGenerated: false,
        createdAt: new Date().toISOString(),
        duplicatedFrom: sourceProjectID,
      });

      const newProjectID = newProjectDoc.id;

      // 3. Copy each step document to the new project
      const writes = progressSnap.docs.map(async (stepDoc) => {
        const newStepRef = doc(
          db,
          "users",
          userID,
          "projects",
          newProjectID,
          "progress",
          stepDoc.id
        );
        await setDoc(newStepRef, stepDoc.data());
      });

      await Promise.all(writes);

      return { newProjectID, stepsCopied: progressSnap.docs.length };
    } catch (err) {
      console.error("Project duplication failed:", err);
      setError("Failed to duplicate project. Please try again.");
      return null;
    } finally {
      setIsDuplicating(false);
    }
  };

  return { duplicateProject, isDuplicating, error };
}
